'use client';

import { useEffect, useState } from 'react';
import { FileClock, Download, ArrowRightCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import {
  listarSolicitudes, listarFirmantes, labelCanal, labelDecision, type Solicitud,
} from '@/lib/preRadicacion';
import { descargarActaReparto } from '@/lib/plantillas/acta-reparto';
import { PlantillaFaltanteError } from '@/lib/plantillas/motor';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

function fmtFecha(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString('es-CO');
}

/**
 * Bandeja de solicitudes de radicación en trámite (EN_PROCESO). No se crean aquí:
 * la solicitud nace y se completa en el Libro radicador (Etapa 1). Aquí se retoman
 * las que quedaron sin terminar («Continuar» vuelve al Libro radicador).
 */
export default function ExpedientesNoIniciados({ onIrARadicar }: { onIrARadicar: () => void }) {
  const [lista, setLista] = useState<Solicitud[]>([]);
  const [cargando, setCargando] = useState(true);

  async function cargar() {
    setCargando(true);
    try {
      setLista(await listarSolicitudes('EN_PROCESO'));
    } catch (e) {
      toast.error('Error al cargar solicitudes', {
        description: e instanceof Error ? e.message : undefined,
      });
    } finally {
      setCargando(false);
    }
  }
  useEffect(() => { cargar(); }, []);

  // Retomar: deja la solicitud pendiente y abre el Libro radicador para continuarla.
  function continuar(s: Solicitud) {
    localStorage.setItem('solicitudPendiente', JSON.stringify({ id: s.id }));
    onIrARadicar();
  }

  async function descargarActa(s: Solicitud) {
    try {
      const firmantes = await listarFirmantes();
      await descargarActaReparto(s, firmantes);
    } catch (e) {
      if (e instanceof PlantillaFaltanteError) {
        toast.error('Falta la plantilla oficial del acta.', {
          description: `Coloca ${e.ruta.split('/').pop()} en FRONTEND/public/assets/plantillas/.`,
        });
      } else {
        toast.error('No se pudo descargar el acta de reparto.', {
          description: e instanceof Error ? e.message : undefined,
        });
      }
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex size-10 items-center justify-center rounded-lg bg-accent text-accent-foreground">
          <FileClock className="size-5" />
        </span>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Expedientes no iniciados</h1>
          <p className="text-sm text-muted-foreground">
            Solicitudes de radicación sin terminar. Retómelas para completar la pre-radicación y pasar al Libro radicador.
          </p>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          {cargando ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Cargando…</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Radicado</TableHead>
                  <TableHead>Fecha ingreso</TableHead>
                  <TableHead>Canal</TableHead>
                  <TableHead>Comité</TableHead>
                  <TableHead>Situación</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lista.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="font-medium">{s.radicado_completo ?? s.radicado_origen ?? '—'}</TableCell>
                    <TableCell>{fmtFecha(s.fecha_ingreso)}</TableCell>
                    <TableCell>{labelCanal(s.canal)}</TableCell>
                    <TableCell>{labelDecision(s.decision_comite)}</TableCell>
                    <TableCell>
                      {s.completa ? (
                        <Badge className="bg-primary text-primary-foreground border-transparent" title="Pre-radicación completa; espera que el contratista/tecnólogo llene la Etapa 2">
                          Pendiente por contratista
                        </Badge>
                      ) : (
                        <Badge variant="secondary" title={s.faltantes.join(' · ')}>
                          <AlertTriangle className="size-3" /> En pre-radicación · {s.faltantes.length} pendiente(s)
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap justify-end gap-1.5">
                        {s.abogado_id && (
                          <Button size="sm" variant="ghost" onClick={() => descargarActa(s)}>
                            <Download className="size-3.5" /> Acta de reparto
                          </Button>
                        )}
                        <Button size="sm" onClick={() => continuar(s)}>
                          <ArrowRightCircle className="size-3.5" /> Continuar
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {lista.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                      Sin solicitudes pendientes. Cree una nueva en el Libro radicador.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
